import { useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useMailPreferences } from '../context/MailPreferencesContext';
import { parseDate } from '../utils/calendarEvents';
import {
  getDesktopNotificationPermission,
  isDocumentVisible,
  notifyCalendarReminder,
} from '../utils/desktopNotifications';

const POLL_MS = 20_000;
const REFRESH_MS = 5 * 60_000;
const FIRED_STORAGE_KEY = 'calendarReminderFired';
const MAX_FIRED = 200;

function loadFired() {
  try {
    const raw = sessionStorage.getItem(FIRED_STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.map(String) : []);
  } catch (_) {
    return new Set();
  }
}

function saveFired(set) {
  try {
    const list = [...set];
    const trimmed = list.length > MAX_FIRED ? list.slice(list.length - MAX_FIRED) : list;
    sessionStorage.setItem(FIRED_STORAGE_KEY, JSON.stringify(trimmed));
  } catch (_) {
    /* ignore */
  }
}

function reminderKey(eventId, minutesBefore, startAt) {
  return `${eventId}|${minutesBefore}|${startAt}`;
}

function formatLeadLabel(minutes) {
  const m = Number(minutes) || 0;
  if (m <= 0) return 'jetzt';
  if (m < 60) return `in ${m} Min.`;
  if (m === 60) return 'in 1 Std.';
  if (m < 1440) return `in ${Math.round(m / 60)} Std.`;
  return 'in 1 Tag';
}

/**
 * App-weite Termin-Erinnerungen (Desktop-Toast + In-App-Hinweis).
 * Lädt Termine separat — überschreibt den Dashboard-/Monats-Kalender nicht.
 */
export default function CalendarReminderWatcher() {
  const { isAuthenticated } = useAuth();
  const { preferences } = useMailPreferences();
  const firedRef = useRef(null);
  const enabledRef = useRef(true);
  const minutesRef = useRef([15]);
  const eventsRef = useRef([]);

  useEffect(() => {
    if (firedRef.current == null) firedRef.current = loadFired();
  }, []);

  useEffect(() => {
    enabledRef.current = preferences.calendarReminders !== false;
    minutesRef.current = Array.isArray(preferences.calendarReminderMinutes)
      ? preferences.calendarReminderMinutes
      : [15];
  }, [preferences.calendarReminders, preferences.calendarReminderMinutes]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    if (preferences.calendarReminders === false) return undefined;

    let cancelled = false;

    const refreshWindow = async () => {
      try {
        const from = new Date();
        from.setHours(0, 0, 0, 0);
        const to = new Date(from);
        to.setDate(to.getDate() + 3);
        const res = await axios.get('/api/calendar-events', {
          params: { from: from.toISOString(), to: to.toISOString() },
        });
        if (!cancelled) {
          eventsRef.current = Array.isArray(res.data?.events) ? res.data.events : [];
        }
      } catch (_) {
        /* quiet */
      }
    };

    const tick = async () => {
      if (cancelled || !enabledRef.current) return;
      if (getDesktopNotificationPermission() !== 'granted' && !isDocumentVisible()) return;

      const now = Date.now();
      const leadTimes = minutesRef.current;
      const fired = firedRef.current || (firedRef.current = loadFired());
      let changed = false;

      for (const ev of eventsRef.current) {
        if (!ev || ev.source === 'campaign' || ev.allDay) continue;
        const start = parseDate(ev.startAt);
        if (!start) continue;
        const startMs = start.getTime();
        if (startMs < now - 60_000) continue;

        for (const minutesBefore of leadTimes) {
          const leadMs = Math.max(0, Number(minutesBefore) || 0) * 60_000;
          const fireAt = startMs - leadMs;
          if (now < fireAt || now > fireAt + 90_000) continue;

          const key = reminderKey(ev.id, minutesBefore, start.toISOString());
          if (fired.has(key)) continue;
          fired.add(key);
          changed = true;

          const shown = await notifyCalendarReminder({
            eventId: ev.id,
            title: ev.title || 'Termin',
            startAt: start.toISOString(),
            minutesBefore,
            location: ev.location || '',
            enabled: true,
            force: true,
          });
          if (shown || isDocumentVisible()) {
            const lead = formatLeadLabel(minutesBefore);
            toast(
              minutesBefore <= 0
                ? `Termin beginnt: ${ev.title || 'Termin'}`
                : `Erinnerung (${lead}): ${ev.title || 'Termin'}`,
              { duration: 6000 }
            );
          }
        }
      }

      if (changed) saveFired(fired);
    };

    refreshWindow();
    tick();
    const pollId = window.setInterval(tick, POLL_MS);
    const refreshId = window.setInterval(refreshWindow, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(pollId);
      window.clearInterval(refreshId);
    };
  }, [isAuthenticated, preferences.calendarReminders]);

  return null;
}
